import React, { useEffect } from 'react'
import { DateInput, DateTimeInput, SelectInput, TextInput } from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'

import {
  formatSubheaderValue,
  TrucksAndTrailersChoice,
  VendorProgram,
  VendorProgramSubheaderType,
} from '../../../models/repayments'

interface Props {
  vendorProgram: VendorProgram
  subheaderValue: string
  setSubheader: (subheader: string) => void
  trucksAndTrailersChoices: TrucksAndTrailersChoice[]
}

function SelectInputOrTextField({
  label,
  source,
  choices,
  onChange,
}: {
  label: string
  source: string
  choices: any[]
  onChange: (e: any) => void
}) {
  return choices.length > 0 ? (
    <SelectInput style={{ flex: 1 }} label={label} source={source} choices={choices} onChange={onChange} />
  ) : (
    <TextInput style={{ flex: 1 }} label={label} source={source} onChange={onChange} />
  )
}

function SubheaderForm({ vendorProgram, setSubheader, trucksAndTrailersChoices }: Props) {
  if (vendorProgram.subheader === VendorProgramSubheaderType.TRAILER_ID) {
    return (
      <SelectInputOrTextField
        label="Trailer ID"
        source="trailer_id"
        choices={vendorProgram.subheader_items.map((item) => ({
          id: item,
          name: trucksAndTrailersChoices.find((trailer) => trailer.vin_number === item)?.name || item,
        }))}
        onChange={(e) => setSubheader(e.target.value)}
      />
    )
  }

  if (vendorProgram.subheader === VendorProgramSubheaderType.TEAM_NAME) {
    return (
      <SelectInputOrTextField
        label="Team Name"
        source="team_name"
        choices={vendorProgram.subheader_items.map((item) => ({ id: item, name: item }))}
        onChange={(e) => setSubheader(e.target.value)}
      />
    )
  }

  if (vendorProgram.subheader === VendorProgramSubheaderType.VIN_NUMBER) {
    return (
      <SelectInputOrTextField
        label="Associated VIN number"
        source="vin_number"
        choices={vendorProgram.subheader_items.map((item) => ({
          id: item,
          name: trucksAndTrailersChoices.find((truck) => truck.vin_number === item)?.name || item,
        }))}
        onChange={(e) => setSubheader(e.target.value)}
      />
    )
  }

  if (vendorProgram.subheader === VendorProgramSubheaderType.PLATES_NUMBER) {
    return (
      <SelectInputOrTextField
        label="Associated license plate number"
        source="plates_number"
        choices={vendorProgram.subheader_items.map((item) => ({
          id: item,
          name:
            trucksAndTrailersChoices.find((truckOrTrailer) => truckOrTrailer.license_plate_number === item)
              ?.name || item,
        }))}
        onChange={(e) => setSubheader(e.target.value)}
      />
    )
  }

  if (vendorProgram.subheader === VendorProgramSubheaderType.DRIVER_NAME) {
    return (
      <SelectInputOrTextField
        label="Associated driver name"
        source="driver_name"
        choices={vendorProgram.subheader_items.map((item) => ({ id: item, name: item }))}
        onChange={(e) => setSubheader(e.target.value)}
      />
    )
  }

  if (vendorProgram.subheader === VendorProgramSubheaderType.JOB_ID) {
    return (
      <SelectInputOrTextField
        label="Job ID"
        source="job_id"
        choices={vendorProgram.subheader_items.map((item) => ({ id: item, name: item }))}
        onChange={(e) => setSubheader(e.target.value)}
      />
    )
  }

  if (vendorProgram.subheader === VendorProgramSubheaderType.UNDERPAYMENT) {
    return (
      <DateInput
        style={{ flex: 1 }}
        label="Underpayment for"
        source="underpayment_date"
        onChange={(e) => setSubheader(e.target.value)}
      />
    )
  }

  if (vendorProgram.subheader === VendorProgramSubheaderType.PAID_BY_CT_ON_DATE) {
    return (
      <DateInput
        style={{ flex: 1 }}
        label="Paid by CT on"
        source="paid_by_ct_on_date"
        onChange={(e) => setSubheader(e.target.value)}
      />
    )
  }

  if (vendorProgram.subheader === VendorProgramSubheaderType.DATE_INSTANCE_OCCURRED) {
    return (
      <DateTimeInput
        style={{ flex: 1 }}
        label="Date instance occurred"
        source="date_instance_occurred"
        onChange={(e) => setSubheader(e.target.value)}
      />
    )
  }

  if (vendorProgram.subheader === VendorProgramSubheaderType.NOTHING_TO_FILL) {
    return null
  }

  return null
}

export function PolicySubheader({
  vendorProgram,
  subheaderValue,
  setSubheader,
  trucksAndTrailersChoices,
}: Props) {
  useEffect(() => {
    setSubheader(formatSubheaderValue(vendorProgram.subheader, ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorProgram])

  return (
    <FlexBox width="100%" isColumn alignItems="stretch">
      <SubheaderForm
        setSubheader={(value) => setSubheader(formatSubheaderValue(vendorProgram.subheader, value))}
        subheaderValue={subheaderValue}
        vendorProgram={vendorProgram}
        trucksAndTrailersChoices={trucksAndTrailersChoices}
      />
    </FlexBox>
  )
}
